// src/services/TeamService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Unsubscribe,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import {
  Member,
  Role,
  Invite,
  InviteMemberInput,
  SetMemberRolesInput,
  SetMemberStatusInput,
  DeleteInviteInput,
  MemberStatus,
  UserProfile,
} from "@visitproof/types";

export interface MemberWithProfile extends Member {
  id: string;
  profile?: Pick<UserProfile, 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'avatarUrl'>;
}

export interface RoleWithId extends Role {
  id: string;
}

export interface InviteWithId extends Invite {
  id: string;
}

export const TeamService = {
  /**
   * Subscribe to members of an account
   */
  subscribeToMembers(
    accountId: string,
    callback: (members: MemberWithProfile[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const membersRef = collection(db, "accounts", accountId, "members");
    const q = query(membersRef);

    return onSnapshot(
      q,
      async (snapshot) => {
        const allMembers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MemberWithProfile[];

        // Filter out parents and supervisors - Team page only shows staff (owner/admin)
        const staffMembers = allMembers.filter((member) => {
          const roleIds = member.roleIds || [];
          // Include if member has owner or admin role
          return roleIds.includes('owner') || roleIds.includes('admin');
        });

        // Fetch user profiles for display
        const uids = staffMembers.map((m) => m.id);
        const profiles = await TeamService.fetchUserProfiles(uids);

        const membersWithProfiles = staffMembers.map((member) => ({
          ...member,
          profile: profiles[member.id],
        }));

        callback(membersWithProfiles);
      },
      (error) => {
        console.error("Error subscribing to members:", error);
        onError?.(error);
      }
    );
  },

  /**
   * Fetch user profiles by UIDs
   */
  async fetchUserProfiles(
    uids: string[]
  ): Promise<Record<string, Pick<UserProfile, 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'avatarUrl'>>> {
    const profiles: Record<string, Pick<UserProfile, 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'avatarUrl'>> = {};

    // Fetch each user document directly by ID. Previously this scanned the
    // ENTIRE users collection once per member (N x full-collection read, and
    // permission-denied under least-privilege rules).
    const userDocs = await Promise.all(
      uids.map(async (uid) => {
        try {
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            return {
              uid,
              profile: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNumber: data.phoneNumber,
                avatarUrl: data.avatarUrl,
              },
            };
          }
          return null;
        } catch {
          return null;
        }
      })
    );

    userDocs.forEach((result) => {
      if (result) {
        profiles[result.uid] = result.profile;
      }
    });

    return profiles;
  },

  /**
   * Subscribe to roles of an account
   */
  subscribeToRoles(
    accountId: string,
    callback: (roles: RoleWithId[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const rolesRef = collection(db, "accounts", accountId, "roles");
    const q = query(rolesRef);

    return onSnapshot(
      q,
      (snapshot) => {
        const roles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as RoleWithId[];
        callback(roles);
      },
      (error) => {
        console.error("Error subscribing to roles:", error);
        onError?.(error);
      }
    );
  },

  /**
   * Subscribe to invites of an account
   */
  subscribeToInvites(
    accountId: string,
    callback: (invites: InviteWithId[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const invitesRef = collection(db, "accounts", accountId, "invites");
    const q = query(invitesRef);

    return onSnapshot(
      q,
      (snapshot) => {
        const invites = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as InviteWithId[];
        callback(invites);
      },
      (error) => {
        console.error("Error subscribing to invites:", error);
        onError?.(error);
      }
    );
  },

  /**
   * Get all roles for an account
   */
  async getRoles(accountId: string): Promise<RoleWithId[]> {
    try {
      const snapshot = await getDocs(
        collection(db, "accounts", accountId, "roles")
      );
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as RoleWithId[];
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw new Error(`Failed to get roles: ${error}`);
    }
  },

  /**
   * Invite a new member to the account
   */
  async inviteMember(input: InviteMemberInput): Promise<{ inviteId: string }> {
    try {
      const inviteMemberFn = httpsCallable<
        InviteMemberInput,
        { success: boolean; inviteId: string }
      >(functions, "inviteMember");

      const result = await inviteMemberFn(input);
      return { inviteId: result.data.inviteId };
    } catch (error) {
      console.error("Error inviting member:", error);
      throw error;
    }
  },

  /**
   * Update a member's roles
   */
  async setMemberRoles(
    accountId: string,
    targetUid: string,
    roleIds: string[]
  ): Promise<void> {
    try {
      const setMemberRolesFn = httpsCallable<
        SetMemberRolesInput,
        { success: boolean }
      >(functions, "setMemberRoles");

      await setMemberRolesFn({ accountId, targetUid, roleIds });
    } catch (error) {
      console.error("Error setting member roles:", error);
      throw error;
    }
  },

  /**
   * Update a member's status
   */
  async setMemberStatus(
    accountId: string,
    targetUid: string,
    status: MemberStatus
  ): Promise<void> {
    try {
      const setMemberStatusFn = httpsCallable<
        SetMemberStatusInput,
        { success: boolean }
      >(functions, "setMemberStatus");

      await setMemberStatusFn({ accountId, targetUid, status });
    } catch (error) {
      console.error("Error setting member status:", error);
      throw error;
    }
  },

  /**
   * Delete a pending invite
   */
  async deleteInvite(accountId: string, inviteId: string): Promise<void> {
    try {
      const deleteInviteFn = httpsCallable<
        DeleteInviteInput,
        { success: boolean }
      >(functions, "deleteInvite");

      await deleteInviteFn({ accountId, inviteId });
    } catch (error) {
      console.error("Error deleting invite:", error);
      throw error;
    }
  },
};
